import { GlobalStyles, useMediaQuery, useTheme } from "@mui/material";
import { driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import { useEffect, useRef } from "react";

import { encampBrandColors } from "../../colors";
import {
  MOBILE_NAV_TOUR_CLASS,
  SPOTLIGHT_POPOVER_CLASS,
  spotlightTourStyles,
} from "./spotlightTourStyles";

const OPEN_NAV_ARIA_LABEL = "Open navigation";
const WAIT_FOR_NAV_TIMEOUT_MS = 2_000;
const WAIT_FOR_NAV_POLL_MS = 50;

export type SpotlightTourStep = {
  /** DOM `id` of the element to highlight. */
  anchorId: string;
  /** Rendered via driver.js's `innerHTML` — keep static; never interpolate untrusted content. */
  title: string;
  /** Rendered via driver.js's `innerHTML` — keep static; never interpolate untrusted content. */
  body: string;
  placement: "top" | "bottom" | "left" | "right";
};

type SpotlightTourProps = {
  /** Must be referentially stable for the life of the tour — a new array identity on every render tears down and restarts driver.js from step 1. Memoize it in the caller. */
  steps: SpotlightTourStep[];
  onDismiss: () => void;
  onComplete: () => void;
  onStepViewed: (step: number) => void;
  /**
   * Anchor that only exists inside the temporary left-nav drawer on small
   * screens. Set it and the tour will open the drawer and raise it above the
   * driver.js overlay before highlighting. Omit for anchors always on-page.
   */
  mobileNavAnchorId?: string;
};

function openMobileNavDrawerIfNeeded(anchorId: string) {
  if (document.getElementById(anchorId)) {
    return;
  }
  const menuButton = document.querySelector<HTMLButtonElement>(
    `[aria-label="${OPEN_NAV_ARIA_LABEL}"]`
  );
  menuButton?.click();
}

function isReadyForHighlight(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  // Temporary drawer slides in from the left; wait until the row is on-screen.
  return rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.left < window.innerWidth;
}

function waitForAnchorElement(anchorId: string): Promise<HTMLElement | null> {
  const existing = document.getElementById(anchorId);
  if (existing && isReadyForHighlight(existing)) {
    return Promise.resolve(existing);
  }

  return new Promise((resolve) => {
    const startedAt = Date.now();

    const poll = () => {
      const element = document.getElementById(anchorId);
      if (element && isReadyForHighlight(element)) {
        resolve(element);
        return;
      }
      if (Date.now() - startedAt >= WAIT_FOR_NAV_TIMEOUT_MS) {
        resolve(element);
        return;
      }
      window.setTimeout(poll, WAIT_FOR_NAV_POLL_MS);
    };

    poll();
  });
}

function setMobileNavTourStacking(enabled: boolean) {
  document.documentElement.classList.toggle(MOBILE_NAV_TOUR_CLASS, enabled);
}

function buildDriveSteps(
  steps: SpotlightTourStep[],
  isMobileDrawerMode: boolean,
  mobileNavAnchorId: string | undefined
): DriveStep[] {
  return steps.map((step, index) => ({
    element: `#${step.anchorId}`,
    popover: {
      title: step.title,
      description: step.body,
      // Full-bleed temporary drawers leave no room to the right of the nav row.
      side: step.anchorId === mobileNavAnchorId && isMobileDrawerMode ? "bottom" : step.placement,
      align: "start",
      showButtons: ["next", "close"],
      // Leaving `nextBtnText` unset lets driver.js swap to `doneBtnText` on the
      // last step — an explicit value here would clobber that swap, since
      // driver.js merges the per-step popover object after computing it.
      doneBtnText: "Got it",
      showProgress: true,
      progressText: `${index + 1} of ${steps.length}`,
    },
  }));
}

/**
 * driver.js spotlight tour over real page elements. Steps address their targets
 * by DOM `id`, so anchors must be rendered before the tour mounts.
 */
export function SpotlightTour({
  steps,
  onDismiss,
  onComplete,
  onStepViewed,
  mobileNavAnchorId,
}: SpotlightTourProps) {
  const theme = useTheme();
  const isMobileDrawerMode = useMediaQuery(theme.breakpoints.down("md"));
  const completedRef = useRef(false);
  const advancingToNavRef = useRef(false);
  const onDismissRef = useRef(onDismiss);
  const onCompleteRef = useRef(onComplete);
  const onStepViewedRef = useRef(onStepViewed);

  useEffect(() => {
    onDismissRef.current = onDismiss;
    onCompleteRef.current = onComplete;
    onStepViewedRef.current = onStepViewed;
  }, [onDismiss, onComplete, onStepViewed]);

  useEffect(() => {
    let destroyedByCleanup = false;
    completedRef.current = false;
    advancingToNavRef.current = false;

    const mobileNavStepIndex = mobileNavAnchorId
      ? steps.findIndex((step) => step.anchorId === mobileNavAnchorId)
      : -1;

    const driverObj = driver({
      animate: true,
      allowClose: true,
      overlayColor: encampBrandColors.richBlack,
      overlayOpacity: 0.55,
      stagePadding: 4,
      stageRadius: 12,
      popoverOffset: 12,
      popoverClass: SPOTLIGHT_POPOVER_CLASS,
      disableActiveInteraction: true,
      steps: buildDriveSteps(steps, isMobileDrawerMode, mobileNavAnchorId),
      onHighlighted: (_element, _step, { driver: activeDriver }) => {
        const activeIndex = activeDriver.getActiveIndex() ?? -1;
        if (activeIndex >= 0) {
          onStepViewedRef.current(activeIndex + 1);
        }
      },
      onNextClick: (_element, _step, { driver: activeDriver }) => {
        if (activeDriver.isLastStep()) {
          completedRef.current = true;
          activeDriver.destroy();
          return;
        }

        const activeIndex = activeDriver.getActiveIndex() ?? 0;
        const advancingToMobileNav =
          mobileNavStepIndex >= 0 && activeIndex + 1 === mobileNavStepIndex;

        if (!isMobileDrawerMode || !advancingToMobileNav || advancingToNavRef.current) {
          activeDriver.moveNext();
          return;
        }

        // Open the temporary drawer and wait for the anchor before highlighting.
        // Otherwise driver.js falls back to a center dummy and refresh() keeps that
        // dummy — and the drawer stays under the overlay (z-index ~1300 vs 10000).
        advancingToNavRef.current = true;
        if (mobileNavAnchorId) {
          openMobileNavDrawerIfNeeded(mobileNavAnchorId);
        }
        void waitForAnchorElement(mobileNavAnchorId ?? "").then((element) => {
          advancingToNavRef.current = false;
          if (destroyedByCleanup || !activeDriver.isActive()) {
            return;
          }
          if (element) {
            setMobileNavTourStacking(true);
          }
          activeDriver.moveNext();
        });
      },
      onCloseClick: (_element, _step, { driver: activeDriver }) => {
        activeDriver.destroy();
      },
      onDestroyed: () => {
        setMobileNavTourStacking(false);
        if (destroyedByCleanup) {
          return;
        }
        if (completedRef.current) {
          onCompleteRef.current();
          return;
        }
        onDismissRef.current();
      },
    });

    // Wait a frame so Dialog teardown has finished and anchors are measurable.
    const frameId = window.requestAnimationFrame(() => {
      driverObj.drive();
    });

    return () => {
      destroyedByCleanup = true;
      window.cancelAnimationFrame(frameId);
      setMobileNavTourStacking(false);
      driverObj.destroy();
    };
  }, [isMobileDrawerMode, steps, mobileNavAnchorId]);

  return <GlobalStyles styles={spotlightTourStyles(theme)} />;
}
